import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Autocomplete, Chip, InputAdornment, TextField } from "@mui/material";
import { Search } from "@mui/icons-material";
import { programLevel, states } from "const/constants";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { PageHeader, EmptyState, LoadingPanel, ErrorPanel } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerCourses } from "../../api/endpoints/workspace";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { courseDuration, courseMoney } from "./courseHelpers";

const PAGE_SIZE = 24;

const coursesKey = (params) => [...partnerQueryKeys.all, "courses", params];

const FIELD_SX = { backgroundColor: "white", borderRadius: "12px", minWidth: 200 };

const valuesOf = (options) => options.map((option) => option.value);

/**
 * The course catalogue as a partner uses it: search and narrow, then read the fees and intakes
 * without opening anything. Served by `/partner/courses`, which scopes to the courses this
 * organization can see.
 */
const CoursesPage = () => {
  const [search, setSearch] = useState("");
  const [university, setUniversity] = useState("");
  const [levels, setLevels] = useState([]);
  const [intake, setIntake] = useState("");
  const [stateFilter, setStateFilter] = useState([]);
  const [page, setPage] = useState(0);

  // Free text fires a request per keystroke without this; the inputs stay instant while the
  // query waits for a pause.
  const debouncedSearch = useDebouncedValue(search.trim(), 400);
  const debouncedUniversity = useDebouncedValue(university.trim(), 400);
  const debouncedIntake = useDebouncedValue(intake.trim(), 400);

  const params = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(debouncedUniversity ? { universityName: debouncedUniversity } : {}),
      ...(levels.length ? { levels: valuesOf(levels) } : {}),
      // Free text, not the old full-month dropdown: courses store shorthand ("Feb", "Sept"),
      // which a full name never matched. The server matches case-insensitively from the
      // start, so "feb" finds both "Feb" and "February".
      ...(debouncedIntake ? { intakes: [debouncedIntake] } : {}),
      ...(stateFilter.length ? { states: valuesOf(stateFilter) } : {}),
    }),
    [page, debouncedSearch, debouncedUniversity, levels, debouncedIntake, stateFilter]
  );

  const { data, isLoading, isError, refetch } = usePartnerQuery(
    coursesKey(params),
    () => fetchPartnerCourses(params),
    { keepPreviousData: true }
  );

  const rows = data?.content || [];
  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.totalItems ?? rows.length;
  const filtered =
    Boolean(search.trim() || university.trim() || intake.trim()) ||
    levels.length > 0 ||
    stateFilter.length > 0;

  const resetFilters = () => {
    setSearch("");
    setUniversity("");
    setLevels([]);
    setIntake("");
    setStateFilter([]);
    setPage(0);
  };

  const onFilterChange = (setter) => (_event, value) => {
    setter(value);
    setPage(0);
  };

  return (
    <div>
      <PageHeader title="Courses" />

      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
        <div className="flex flex-wrap gap-3">
          <TextField
            size="small"
            label="Course, level or place"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            sx={{ ...FIELD_SX, flex: "1 1 260px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" className="text-slate-400" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            label="University"
            value={university}
            onChange={(event) => {
              setUniversity(event.target.value);
              setPage(0);
            }}
            sx={{ ...FIELD_SX, flex: "1 1 200px" }}
          />
          <Autocomplete
            multiple
            size="small"
            options={programLevel}
            value={levels}
            onChange={onFilterChange(setLevels)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            sx={{ ...FIELD_SX, flex: "1 1 220px" }}
            renderInput={(inputProps) => <TextField {...inputProps} label="Level" />}
          />
          <TextField
            size="small"
            label="Intake"
            placeholder="e.g. Feb"
            value={intake}
            onChange={(event) => {
              setIntake(event.target.value);
              setPage(0);
            }}
            sx={{ ...FIELD_SX, flex: "1 1 200px" }}
          />
          <Autocomplete
            multiple
            size="small"
            options={states}
            value={stateFilter}
            onChange={onFilterChange(setStateFilter)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            sx={{ ...FIELD_SX, flex: "1 1 220px" }}
            renderInput={(inputProps) => <TextField {...inputProps} label="State" />}
          />
        </div>

        <div className="flex items-center justify-between gap-3 mt-3">
          <p className="text-xs text-slate-500 m-0">
            {isLoading ? "Searching…" : `${totalItems} course${totalItems === 1 ? "" : "s"}`}
          </p>
          {filtered ? (
            <button
              type="button"
              onClick={resetFilters}
              className="bg-transparent border-0 p-0 text-xs font-semibold text-teal-700 cursor-pointer"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <LoadingPanel label="Searching the catalogue…" />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={filtered ? "No course matches those filters" : "No courses listed"}
          description={
            filtered
              ? "Widen the search, or clear the filters to see the whole catalogue."
              : "The course catalogue is empty."
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {rows.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 mt-4">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

const CourseCard = ({ course }) => {
  const place = [course.state, course.country].filter(Boolean).join(", ");
  const perSem = courseMoney(course.feePerSem);
  const total = courseMoney(course.totalFees);
  const duration = courseDuration(course.duration);
  return (
    <article className="flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <header className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold m-0 leading-snug">
          <Link
            to={`/partner/courses/${course.id}`}
            className="text-slate-900 no-underline hover:text-teal-700"
          >
            {course.courseName || "Untitled course"}
          </Link>
        </h3>
        <p className="text-xs text-slate-500 m-0 mt-1">
          {course.universityName || "University not set"}
          {place ? ` · ${place}` : ""}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {course.level ? (
            <Chip size="small" label={course.level} sx={{ height: 20, fontSize: 11 }} />
          ) : null}
          {duration ? (
            <Chip size="small" label={duration} sx={{ height: 20, fontSize: 11 }} />
          ) : null}
          {course.maxScholarship ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`Up to ${course.maxScholarship}% scholarship`}
              sx={{ height: 20, fontSize: 11 }}
            />
          ) : null}
        </div>
      </header>

      <dl className="grid grid-cols-3 gap-px bg-slate-100 m-0">
        <Fact label="Per sem" value={perSem} />
        <Fact label="Total" value={total} />
        <Fact label="Semesters" value={course.noOfSem} />
      </dl>

      {course.intakes?.length ? (
        <div className="px-4 py-3 mt-auto">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0 mb-1.5">
            Intakes
          </p>
          <div className="flex flex-wrap gap-1.5">
            {course.intakes.map((intake) => (
              <span
                key={intake}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
              >
                {intake}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
};

const Fact = ({ label, value }) => (
  <div className="bg-white px-4 py-3">
    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0">
      {label}
    </dt>
    <dd className="text-sm font-semibold text-slate-900 m-0 mt-0.5 tabular-nums">{value || "—"}</dd>
  </div>
);

export default CoursesPage;
